import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  catchError,
  map,
  Observable,
  of,
  OperatorFunction,
  retry,
  switchMap,
  throwError
} from 'rxjs';
import { ApiSchema } from './http-api-schema';
import { ApiException } from '@helpers/errors/api-error';
import { HttpTranslatorDecorator } from './http-translate-decorator';
import { APP_CONFIG } from '@cfg/env/app.config';

/**
 * Servicio para realizar simplificar la ejecución de solicitudes HTTP hacia una API
 */
@Injectable({
  providedIn: 'root'
})
export class AppHttpClientApi {
  /**
   * Configuración de la aplicación
   */
  private readonly appConfig = inject(APP_CONFIG);

  /**
   * Servicio HttpClient de angular
   */
  private readonly httpClient = inject(HttpClient);

  /**
   * Decorador para traducir los mensajes de respuesta de la API
   */
  private readonly translateDecorator = inject(HttpTranslatorDecorator);

  /**
   * Opciones comunes para las solicitudes HTTP
   */
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  /**
   * Configuración por defecto para reintentos de solicitudes HTTP
   */
  private readonly defaultRetries = { count: 1, delay: 200 };

  /**
   * Realiza una solicitud HTTP de tipo GET
   * @param url URL de la solicitud
   */
  get<T>(url: string): Observable<T | undefined> {
    return this.httpClient
      .get<
        ApiSchema<T>
      >(`${this.appConfig.backend.context}/${url}`, this.httpOptions)
      .pipe(
        retry(this.defaultRetries),
        this.handleError<T>(),
        this.handleResponse<T>()
      );
  }

  /**
   * Realiza una solicitud HTTP de tipo POST
   * @param url URL de la solicitud
   * @param body Cuerpo de la solicitud
   */
  post<T>(
    url: string,
    body: unknown,
    customHeaders?: HttpHeaders
  ): Observable<T | undefined> {
    const opts = {
      headers: this.httpOptions.headers
    };
    if (customHeaders) {
      opts.headers = customHeaders;
    }

    return this.httpClient
      .post<
        ApiSchema<T>
      >(`${this.appConfig.backend.context}/${url}`, body, opts)
      .pipe(
        retry(this.defaultRetries),
        this.handleError<T>(),
        this.handleResponse<T>()
      );
  }

  /**
   * Realiza una solicitud HTTP POST retornando la respuesta cruda sin aplicar el esquema estándar.
   * Útil para endpoints que no siguen el contrato ApiSchema.
   */
  postPlain<T>(
    url: string,
    body: unknown,
    customHeaders?: HttpHeaders
  ): Observable<T | undefined> {
    let headers = customHeaders ?? this.httpOptions.headers;

    if (body instanceof FormData && headers.has('Content-Type')) {
      headers = headers.delete('Content-Type');
    }

    const opts = { headers };

    return this.httpClient
      .post<T>(`${this.appConfig.backend.context}/${url}`, body, opts)
      .pipe(
        retry(this.defaultRetries),
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            const schema = err.error as ApiSchema<T> | undefined;
            if (schema?.errors && schema.errors.length > 0) {
              return this.translateDecorator
                .translate(schema)
                .pipe(
                  switchMap((response) =>
                    throwError(() => new ApiException(response.errors ?? []))
                  )
                );
            }
            const body = err.error as {
              message?: string;
              code?: string;
            } | null;
            if (body?.message) {
              return throwError(
                () =>
                  new ApiException([
                    {
                      key: body.code ?? 'UNKNOWN_ERROR',
                      message: body.message!
                    }
                  ])
              );
            }
          }

          return throwError(() => err);
        })
      );
  }

  /**
   * Realiza una solicitud HTTP GET retornando la respuesta cruda sin aplicar el esquema estándar.
   * Útil para endpoints que no siguen el contrato ApiSchema.
   */
  getPlain<T>(url: string): Observable<T | undefined> {
    return this.httpClient
      .get<T>(`${this.appConfig.backend.context}/${url}`, this.httpOptions)
      .pipe(
        retry(this.defaultRetries),
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            const schema = err.error as ApiSchema<T> | undefined;
            if (schema?.errors && schema.errors.length > 0) {
              return this.translateDecorator
                .translate(schema)
                .pipe(
                  switchMap((response) =>
                    throwError(() => new ApiException(response.errors ?? []))
                  )
                );
            }
            const body = err.error as {
              message?: string;
              code?: string;
            } | null;
            if (body?.message) {
              return throwError(
                () =>
                  new ApiException([
                    {
                      key: body.code ?? 'UNKNOWN_ERROR',
                      message: body.message!
                    }
                  ])
              );
            }
          }

          return throwError(() => err);
        })
      );
  }

  /**
   * Realiza una solicitud HTTP de tipo PUT
   * @param url URL de la solicitud
   * @param body Cuerpo de la solicitud
   */
  put<T>(url: string, body: unknown): Observable<T | undefined> {
    return this.httpClient
      .put<
        ApiSchema<T>
      >(`${this.appConfig.backend.context}/${url}`, body, this.httpOptions)
      .pipe(
        retry(this.defaultRetries),
        this.handleError<T>(),
        this.handleResponse<T>()
      );
  }

  delete(url: string): Observable<void> {
    return this.httpClient
      .delete(`${this.appConfig.backend.context}/${url}`, this.httpOptions)
      .pipe(
        retry(this.defaultRetries),
        map(() => undefined),
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            const schema = err.error as ApiSchema<unknown> | undefined;

            if (schema?.errors?.length) {
              return of(schema);
            }

            const body = err.error as {
              code?: string;
              message?: string;
            } | null;

            if (body?.message) {
              return of({
                errors: [
                  {
                    key: body.code ?? 'UNKNOWN_ERROR',
                    message: body.message
                  }
                ]
              } as ApiSchema<unknown>);
            }
          }

          throw err;
        }),
        switchMap((response) => {
          if (response) {
            throw new ApiException(response.errors ?? []);
          }

          return of(undefined);
        })
      );
  }

  private handleError<T>(): OperatorFunction<ApiSchema<T>, ApiSchema<T>> {
    return catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        const schema = err.error as ApiSchema<T>;
        return this.translateDecorator.translate(schema).pipe(
          map((response) => {
            if (response.errors && response.errors.length > 0) {
              throw new ApiException(response.errors);
            }
            return response;
          })
        );
      }

      throw err;
    });
  }

  private handleResponse<T>() {
    return (
      switchMap((response: ApiSchema<T>) =>
        this.translateDecorator.translate(response)
      ),
      map((response: ApiSchema<T>) => response.results)
    );
  }
}
